"use client";

import { createElement, Fragment, useEffect, useRef, useState, useMemo } from "react";
import { createRoot, type Root } from "react-dom/client";
import { useSearchBox, usePagination } from "react-instantsearch";
import { autocomplete } from "@algolia/autocomplete-js";
import { createLocalStorageRecentSearchesPlugin } from "@algolia/autocomplete-plugin-recent-searches";
import { createQuerySuggestionsPlugin } from "@algolia/autocomplete-plugin-query-suggestions";
import debounce from "@/lib/debounce";
import profile from "@/profiles/profileSingleton";
import { useSearchLabels } from "./SearchLabelsContext";
import "@algolia/autocomplete-theme-classic";
const algoliaAppId = profile.NEXT_PUBLIC_ALGOLIA_APP_ID || "";
const algoliaAPIKey = profile.NEXT_PUBLIC_ALGOLIA_API_KEY || "";
const qsIndexName = profile.NEXT_PUBLIC_ALGOLIA_QS_INDEX || "";
interface CommonSearch {
  searchString: string;
  id?: {
    id: string;
  };
  filterList?: string[];
}
interface AutocompleteSearchBoxProps {
  searchPlaceholderText?: string;
  currentRefinement: string;
  setCurrentRefinement: (value: string) => void;
  commonSearches?: CommonSearch[];
  recentSearchesLabel?: string;
  commonSearchesLabel?: string;
  onClear?: () => void;
  // OneTrust-derived consent. When false the recent-searches localStorage
  // plugin is omitted, so `nav-search-recents` is never written and any
  // pre-existing entries (cleared by AlgoliaInsightsBootstrap on revoke)
  // stay cleared.
  hasAlgoliaConsent?: boolean;
}
export default function AutocompleteSearchBox({
  searchPlaceholderText,
  currentRefinement,
  setCurrentRefinement,
  commonSearches,
  recentSearchesLabel,
  commonSearchesLabel,
  onClear,
  hasAlgoliaConsent = false
}: AutocompleteSearchBoxProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const panelContainerRef = useRef<HTMLDivElement>(null);
  const panelRootRef = useRef<Root | null>(null);
  const rootRef = useRef<HTMLElement | null>(null);
  const autocompleteRef = useRef<ReturnType<typeof autocomplete> | null>(null);
  // Tracks the query value most recently emitted *by the widget itself*
  // (via onStateChange, below), so the sync effect further down can tell
  // the difference between an echo of the user's own typing and a change
  // that genuinely originated elsewhere (URL deep-link, common-search
  // selection, clear button). Without this, every keystroke round-trips
  // through parent state and back, forcing a redundant setQuery()+refresh()
  // on the widget mid-type — which both re-fetches sources unnecessarily
  // (feels like a reload) and can stomp the input's live value, dropping
  // characters while typing.
  const lastEmittedQueryRef = useRef<string | undefined>(undefined);
  const labels = useSearchLabels();

  // Props from siteApplicationData take priority; searchLabels fill the gaps.
  const placeholderText = searchPlaceholderText || labels.search;
  const recentLabel = recentSearchesLabel || labels.recentSearches;
  const commonLabel = commonSearchesLabel || labels.commonSearches;
  const {
    refine: setQuery
  } = useSearchBox();
  const {
    refine: setPage
  } = usePagination();
  const [instantSearchUiState, setInstantSearchUiState] = useState({
    query: currentRefinement
  });
  const [algoliaSearchClient, setAlgoliaSearchClient] = useState<any>(null);
  useEffect(() => {
    if (!algoliaAppId || !algoliaAPIKey || !qsIndexName) return;
    let cancelled = false;
    import("algoliasearch/lite").then(({
      liteClient
    }) => {
      if (cancelled) return;
      setAlgoliaSearchClient(liteClient(algoliaAppId, algoliaAPIKey));
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const debouncedSetState = useMemo(() => debounce(setInstantSearchUiState, 300), []);
  useEffect(() => {
    setQuery(instantSearchUiState.query);
    setPage(0);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [instantSearchUiState]);
  const plugins = useMemo(() => {
    // Only register the localStorage recents plugin when the visitor has
    // accepted the configured OneTrust category. Without consent we omit
    // it entirely so the `nav-search-recents` key is never written;
    // `AlgoliaInsightsBootstrap` is responsible for purging any pre-
    // existing entries on revoke.
    const recentSearches = hasAlgoliaConsent ? createLocalStorageRecentSearchesPlugin({
      key: "nav-search-recents",
      limit: 3,
      transformSource({
        source
      }) {
        return {
          ...source,
          templates: {
            ...source.templates,
            header({
              items
            }: {
              items: any[];
            }) {
              if (!items.length) return null;
              return createElement(Fragment, null, createElement("span", {
                className: "aa-SourceHeaderTitle"
              }, recentLabel), createElement("span", {
                className: "aa-SourceHeaderLine"
              }));
            }
          },
          onSelect({
            item
          }: {
            item: any;
          }) {
            setInstantSearchUiState({
              query: item.label
            });
            setCurrentRefinement(item.label);
          }
        } as any;
      }
    }) : null;
    const list: any[] = recentSearches ? [recentSearches] : [];
    if (qsIndexName && algoliaSearchClient) {
      const querySuggestions = createQuerySuggestionsPlugin({
        searchClient: algoliaSearchClient as any,
        indexName: qsIndexName,
        getSearchParams() {
          // The query-suggestions plugin can chain off the recents
          // plugin's params. When recents is disabled (no consent) fall
          // back to a fixed hitsPerPage instead.
          if (recentSearches) {
            return recentSearches.data!.getAlgoliaSearchParams({
              hitsPerPage: 3
            });
          }
          return {
            hitsPerPage: 3
          };
        },
        transformSource({
          source
        }) {
          return {
            ...source,
            sourceId: "querySuggestionsPlugin",
            templates: {
              ...source.templates,
              header({
                items
              }: {
                items: any[];
              }) {
                if (!items.length) return null;
                return createElement(Fragment, null, createElement("span", {
                  className: "aa-SourceHeaderTitle"
                }, labels.suggestions), createElement("span", {
                  className: "aa-SourceHeaderLine"
                }));
              }
            },
            onSelect({
              item
            }: {
              item: any;
            }) {
              setInstantSearchUiState({
                query: item.query
              });
              setCurrentRefinement(item.query);
            },
            getItems(params: any) {
              if (!params.state.query) return [];
              return source.getItems(params);
            }
          } as any;
        }
      });
      list.push(querySuggestions);
    }
    return list;
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [hasAlgoliaConsent, algoliaSearchClient]);
  useEffect(() => {
    if (!containerRef.current || !panelContainerRef.current) return;
    autocompleteRef.current?.destroy();
    const previousPanelRoot = panelRootRef.current;
    panelRootRef.current = null;
    rootRef.current = null;
    containerRef.current.innerHTML = "";
    if (previousPanelRoot) {
      // Defer to escape any in-flight React render — see cleanup below.
      queueMicrotask(() => previousPanelRoot.unmount());
    }
    const instance = autocomplete<any>({
      container: containerRef.current,
      panelContainer: panelContainerRef.current,
      placeholder: placeholderText,
      initialState: {
        query: currentRefinement
      },
      openOnFocus: true,
      detachedMediaQuery: "none",
      plugins,
      getSources({
        query
      }: {
        query: string;
      }) {
        if (query || !commonSearches?.length) return [];
        return [{
          sourceId: "commonSearches",
          getItems() {
            return commonSearches.filter((s: CommonSearch) => s.searchString).map((s: CommonSearch) => ({
              label: s.searchString
            }));
          },
          getItemInputValue({
            item
          }: {
            item: any;
          }) {
            return item.label;
          },
          onSelect({
            item
          }: {
            item: any;
          }) {
            setInstantSearchUiState({
              query: item.label
            });
            setCurrentRefinement(item.label);
          },
          templates: {
            header({
              items
            }: {
              items: any[];
            }) {
              if (!items.length) return createElement(Fragment);
              return createElement(Fragment, null, createElement("span", {
                className: "aa-SourceHeaderTitle"
              }, commonLabel), createElement("span", {
                className: "aa-SourceHeaderLine"
              }));
            },
            item({
              item
            }: {
              item: any;
            }) {
              return createElement("div", {
                className: "aa-ItemWrapper"
              }, createElement("div", {
                className: "aa-ItemContent"
              }, createElement("div", {
                className: "aa-ItemContentBody"
              }, createElement("div", {
                className: "aa-ItemContentTitle"
              }, item.label))));
            }
          }
        }];
      },
      onReset() {
        setInstantSearchUiState({
          query: ""
        });
        setCurrentRefinement("");
      },
      onSubmit({
        state
      }: {
        state: any;
      }) {
        setInstantSearchUiState({
          query: state.query
        });
        setCurrentRefinement(state.query);
      },
      onStateChange({
        prevState,
        state
      }: {
        prevState: any;
        state: any;
      }) {
        if (prevState.query !== state.query) {
          lastEmittedQueryRef.current = state.query;
          setCurrentRefinement(state.query);
          debouncedSetState({
            query: state.query
          });
        }
      },
      renderer: {
        createElement,
        Fragment,
        render: () => {}
      },
      render({
        children
      }: {
        children: any;
      }, root: any) {
        if (!panelRootRef.current || rootRef.current !== root) {
          const previousPanelRoot = panelRootRef.current;
          rootRef.current = root;
          panelRootRef.current = createRoot(root);
          if (previousPanelRoot) {
            queueMicrotask(() => previousPanelRoot.unmount());
          }
        }
        panelRootRef.current.render(children);
      }
    } as any);
    autocompleteRef.current = instance;
    return () => {
      instance.destroy();
      const panelRoot = panelRootRef.current;
      panelRootRef.current = null;
      rootRef.current = null;
      autocompleteRef.current = null;
      if (panelRoot) {
        // Defer the unmount to a microtask: when the parent tree is unmounting
        // this component (e.g. closing the search modal), React 18+ throws
        // "Attempted to synchronously unmount a root while React was already
        // rendering" if we unmount the panel's independent root inline here.
        queueMicrotask(() => panelRoot.unmount());
      }
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [plugins]);
  useEffect(() => {
    if (!autocompleteRef.current || currentRefinement === undefined) return;
    // Skip the resync when currentRefinement is just an echo of the value
    // this widget already emitted (normal typing) — otherwise we'd force
    // a redundant setQuery()+refresh() on every keystroke, fighting the
    // widget's own input handling. Only resync for genuinely external
    // updates (e.g. URL deep-link, common-search selection, clear button).
    if (currentRefinement === lastEmittedQueryRef.current) return;
    lastEmittedQueryRef.current = currentRefinement;
    autocompleteRef.current.setQuery(currentRefinement);
    autocompleteRef.current.refresh();
    setInstantSearchUiState({
      query: currentRefinement
    });
  }, [currentRefinement]);
  const handleClear = () => {
    setInstantSearchUiState({
      query: ""
    });
    setCurrentRefinement("");
    if (autocompleteRef.current) {
      autocompleteRef.current.setQuery("");
      autocompleteRef.current.refresh();
    }
    onClear?.();
  };
  return <>
      <div className="nav-search__search-input-row">
        <div className="nav-search__autocomplete" ref={containerRef} />
      </div>
      <div className="nav-search__search-box--blue-divider" />
      {currentRefinement.trim() !== "" && <button className="nav-search__clear-btn" onClick={handleClear} aria-label={labels.resetSearch} type="button">
          {labels.reset}
        </button>}
      <div ref={panelContainerRef} className="nav-search__suggestions-panel" />
    </>;
}